import { Flex } from "components/Flex";
import { Modal } from "components/Modal";
import React, { useState } from "react";
import styled from "styled-components";
import { iconEdit, iconTrash } from "../../assets/icons";
import { useGraphQLAuth } from "../../hooks/useGraphQLAuth";
import { useToast } from "../../hooks/useToast";
import { Button } from "../Button";
import { QuestionUpdateForm } from "../QuestionUpdateForm";
import {
  Container,
  CorrectOption,
  IncorrectOption,
  OptionItem,
  OptionScore,
  OptionsList,
  QuestionHeader,
  QuestionItem,
  QuestionnaireDescription,
  QuestionnaireHeader,
  QuestionnairePassingScore,
  QuestionnaireTitle,
  QuestionTitle,
  ScoreWeight,
} from "./styles";

const DeleteModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  max-width: 500px;
  width: 90vw;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

interface QuestionOption {
  id: string;
  text: string;
  isCorrect: boolean;
  score?: number;
  nextQuestionId?: string;
}

export interface Question {
  questionId: string;
  questionnaireId: string;
  name: string;
  description?: string;
  category?: string;
  type: string;
  options?: QuestionOption[] | null;
  scoreWeight: number;
  allowMultipleAnswers: boolean;
  status: string;
  nextQuestionId?: string;
  insertDate: string;
  updateDate: string;
}

export interface Questionnaire {
  questionnaireId: string;
  name: string;
  description?: string;
  passingScore?: number;
  isPublished: boolean;
  status: string;
  updateDate: string;
  questions: Question[];
}

interface QuestionsListProps {
  questionnaire: Questionnaire;
  onQuestionnaireUpdate?: (updatedQuestionnaire?: Questionnaire) => void;
  onEditClick?: () => void;
}

export const QuestionsList: React.FC<QuestionsListProps> = ({
  questionnaire,
  onQuestionnaireUpdate,
  onEditClick,
}) => {
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState<Question | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteQuestionnaireModalOpen, setIsDeleteQuestionnaireModalOpen] =
    useState(false);
  const [isDeletingQuestionnaire, setIsDeletingQuestionnaire] = useState(false);
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();

  if (
    !questionnaire ||
    !questionnaire.questions ||
    questionnaire.questions.length === 0
  ) {
    return null;
  }

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    }
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setIsUpdateModalOpen(true);
  };

  const handleQuestionUpdateSuccess = (updatedQuestion: Question) => {
    if (onQuestionnaireUpdate) {
      const updatedQuestions = questionnaire.questions.map((q) =>
        q.questionId === updatedQuestion.questionId ? updatedQuestion : q
      );
      const updatedQuestionnaire = {
        ...questionnaire,
        questions: updatedQuestions,
      };
      onQuestionnaireUpdate(updatedQuestionnaire);
    }
    setIsUpdateModalOpen(false);
    setEditingQuestion(null);
  };

  const handleQuestionUpdateCancel = () => {
    setIsUpdateModalOpen(false);
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = (question: Question) => {
    setDeletingQuestion(question);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingQuestion) return;

    setIsDeleting(true);
    try {
      await connectGraphQLApi({
        query: `
          mutation BackofficeDeleteQuestion($questionId: ID!) {
            backofficeDeleteQuestion(questionId: $questionId)
          }
        `,
        variables: {
          questionId: deletingQuestion.questionId,
        },
        errorMessage: "Falha ao deletar pergunta",
        useNewApi: true,
      });

      setToast({
        type: "success",
        message: "Pergunta deletada com sucesso!",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      if (onQuestionnaireUpdate) {
        const updatedQuestions = questionnaire.questions.filter(
          (q) => q.questionId !== deletingQuestion.questionId
        );
        const updatedQuestionnaire = {
          ...questionnaire,
          questions: updatedQuestions,
        };
        onQuestionnaireUpdate(updatedQuestionnaire);
      }

      setIsDeleteModalOpen(false);
      setDeletingQuestion(null);
    } catch (error: any) {
      console.error("Error deleting question:", error);
      setToast({
        type: "error",
        message: error?.message || "Erro ao deletar pergunta",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletingQuestion(null);
  };

  const handleDeleteQuestionnaire = () => {
    setIsDeleteQuestionnaireModalOpen(true);
  };

  const handleDeleteQuestionnaireConfirm = async () => {
    setIsDeletingQuestionnaire(true);
    try {
      await connectGraphQLApi({
        query: `
          mutation BackofficeDeleteQuestionnaire($questionnaireId: ID!) {
            backofficeDeleteQuestionnaire(questionnaireId: $questionnaireId)
          }
        `,
        variables: {
          questionnaireId: questionnaire.questionnaireId,
        },
        errorMessage: "Falha ao deletar questionário",
        useNewApi: true,
      });

      setToast({
        type: "success",
        message: "Questionário deletado com sucesso!",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      setIsDeleteQuestionnaireModalOpen(false);

      // Call the callback to handle questionnaire deletion
      if (onQuestionnaireUpdate) {
        console.log("Calling onQuestionnaireDelete callback");
        onQuestionnaireUpdate(undefined);
      } else {
        console.log("onQuestionnaireDelete callback not provided");
      }
    } catch (error: any) {
      console.error("Error deleting questionnaire:", error);
      setToast({
        type: "error",
        message: error?.message || "Erro ao deletar questionário",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsDeletingQuestionnaire(false);
    }
  };

  const handleDeleteQuestionnaireCancel = () => {
    setIsDeleteQuestionnaireModalOpen(false);
  };

  return (
    <Container>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        margin="0 0 1rem 0"
        gap="0.5rem"
      >
        <QuestionnaireHeader>
          <div>
            <QuestionnaireTitle>{questionnaire.name}</QuestionnaireTitle>
            {questionnaire.description && (
              <QuestionnaireDescription>
                {questionnaire.description}
              </QuestionnaireDescription>
            )}
          </div>
          <QuestionnairePassingScore>
            Pontuação mínima para obter o certificado:{" "}
            {questionnaire.passingScore}
          </QuestionnairePassingScore>
        </QuestionnaireHeader>
        {!questionnaire.isPublished && (
          <Flex gap="0.5rem" style={{ flexShrink: 0 }}>
            <Button
              label="Editar"
              typeBtn="default"
              size="small"
              onClick={handleEditClick}
              type="button"
              icon={iconEdit}
            />
            <Button
              typeBtn="default"
              size="small"
              onClick={handleDeleteQuestionnaire}
              type="button"
              icon={iconTrash}
              onlyIcon
            />
          </Flex>
        )}
      </Flex>
      {questionnaire.questions.map((question, index) => (
        <QuestionItem key={question.questionId}>
          <QuestionHeader>
            <div style={{ flex: 1, minWidth: 0 }}>
              <QuestionTitle>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  {index + 1}. {question.name}
                  {!questionnaire.isPublished && (
                    <Flex gap="0.5rem">
                      <Button
                        typeBtn="default"
                        size="small"
                        onClick={() => handleEditQuestion(question)}
                        type="button"
                        icon={iconEdit}
                        onlyIcon
                      />
                      <Button
                        typeBtn="default"
                        size="small"
                        onClick={() => handleDeleteQuestion(question)}
                        type="button"
                        icon={iconTrash}
                        onlyIcon
                      />
                    </Flex>
                  )}
                </Flex>
                <ScoreWeight>Peso: {question.scoreWeight}</ScoreWeight>
              </QuestionTitle>
              {question.category && (
                <div
                  style={{
                    marginTop: "0.25rem",
                    fontSize: "0.8rem",
                    color: "#6c757d",
                    fontWeight: "500",
                  }}
                >
                  Categoria: {question.category}
                </div>
              )}
            </div>
          </QuestionHeader>
          {question.description && (
            <p
              style={{
                margin: "0.5rem 0",
                fontSize: "0.9rem",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {question.description}
            </p>
          )}
          <OptionsList>
            {question.options && question.options.length > 0 ? (
              question.options.map((option) => (
                <OptionItem key={option.id}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    {option.isCorrect ? (
                      <CorrectOption>
                        <span>{option.text}</span>
                      </CorrectOption>
                    ) : (
                      <IncorrectOption>
                        <span>{option.text}</span>
                      </IncorrectOption>
                    )}
                    {option.score !== undefined && option.score !== null && (
                      <OptionScore>Score: {option.score}</OptionScore>
                    )}
                  </div>
                </OptionItem>
              ))
            ) : question.type === "FREE_TEXT" ? (
              <OptionItem>
                <IncorrectOption>Resposta em texto livre</IncorrectOption>
              </OptionItem>
            ) : (
              <OptionItem>
                <IncorrectOption>Nenhuma opção disponível</IncorrectOption>
              </OptionItem>
            )}
          </OptionsList>
        </QuestionItem>
      ))}

      {editingQuestion && (
        <Modal open={isUpdateModalOpen} onClose={handleQuestionUpdateCancel}>
          <QuestionUpdateForm
            question={editingQuestion}
            onSuccess={handleQuestionUpdateSuccess}
            onCancel={handleQuestionUpdateCancel}
          />
        </Modal>
      )}

      {deletingQuestion && (
        <Modal open={isDeleteModalOpen} onClose={handleDeleteCancel}>
          <DeleteModalContent>
            <h3 style={{ marginBottom: "1rem", color: "#3A3956" }}>
              Confirmar Exclusão
            </h3>
            <p style={{ marginBottom: "2rem", color: "#666" }}>
              Tem certeza que deseja deletar a pergunta{" "}
              <strong>"{deletingQuestion.name}"</strong>? Esta ação não pode ser
              desfeita.
            </p>
            <Flex gap="1rem" justifyContent="center">
              <Button
                label="Cancelar"
                typeBtn="outline"
                onClick={handleDeleteCancel}
                type="button"
              />
              <Button
                label="Deletar"
                typeBtn="danger"
                onClick={handleDeleteConfirm}
                type="button"
                load={isDeleting}
                disable={isDeleting}
              />
            </Flex>
          </DeleteModalContent>
        </Modal>
      )}

      {isDeleteQuestionnaireModalOpen && (
        <Modal
          open={isDeleteQuestionnaireModalOpen}
          onClose={handleDeleteQuestionnaireCancel}
        >
          <DeleteModalContent>
            <h3 style={{ marginBottom: "1rem", color: "#3A3956" }}>
              Confirmar Exclusão do Questionário
            </h3>
            <p style={{ marginBottom: "2rem", color: "#666" }}>
              Tem certeza que deseja deletar o questionário{" "}
              <strong>"{questionnaire.name}"</strong>? Esta ação irá deletar
              todas as perguntas e não pode ser desfeita.
            </p>
            <Flex gap="1rem" justifyContent="center">
              <Button
                label="Cancelar"
                typeBtn="outline"
                onClick={handleDeleteQuestionnaireCancel}
                type="button"
              />
              <Button
                label="Deletar Questionário"
                typeBtn="danger"
                onClick={handleDeleteQuestionnaireConfirm}
                type="button"
                load={isDeletingQuestionnaire}
                disable={isDeletingQuestionnaire}
              />
            </Flex>
          </DeleteModalContent>
        </Modal>
      )}
    </Container>
  );
};
